import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Bell, FileText, MessageSquareText, Search, UserRoundCheck } from "lucide-react";

const lineOfficialUrl = "https://lin.ee/VRC467G";

const navItems = [
  ["หน้าหลัก", "/"],
  ["KHORN Web App", "/product"],
  ["KHORN LINE", "/line"],
  ["KHORN MCP", "/mcp"],
  ["ราคา", "/#pricing"],
  ["ความปลอดภัย", "/privacy"],
  ["ติดต่อ", "/contact"],
];

const capabilities = [
  ["ถามกฎหมายจากมือถือ", "ถามคำถามกฎหมายและให้ KHORN แนบแหล่งอ้างอิงที่ทีมตรวจต่อได้", Search],
  ["ร่างเอกสารเร็ว", "สั่งให้ช่วยร่างหนังสือ สัญญา หรือข้อความตอบลูกความจาก LINE", FileText],
  ["เช็กงานและแจ้งเตือน", "ถามงานค้าง รับการแจ้งเตือน และตามสิ่งที่ยังไม่เสร็จ", Bell],
  ["ข้อมูลลูกความ", "ถามข้อมูลลูกความหรือเคสที่ผูกไว้โดยไม่ต้องเปิดระบบใหญ่", UserRoundCheck],
];

const Header = () => (
  <header className="sticky top-0 z-50 border-b border-[#E8EAE5] bg-white/90 backdrop-blur-xl">
    <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 py-4 md:px-6">
      <Link to="/" className="flex items-center gap-3" aria-label="KHORN AI home">
        <span className="relative block h-11 w-[172px] shrink-0 overflow-hidden">
          <img src="/logo_and_name.png" alt="KHORN AI" className="absolute -left-[28px] -top-[60px] h-[168px] max-w-none" />
        </span>
      </Link>

      <nav className="hidden items-center gap-8 text-[13px] text-[#5F665E] md:flex">
        {navItems.map(([label, href]) => (
          <a key={label} href={href} className="hover:text-[#15130F]">
            {label}
          </a>
        ))}
      </nav>

      <a href={lineOfficialUrl} target="_blank" rel="noopener noreferrer" className="rounded-full bg-[#06C755] px-5 py-2.5 text-[13px] font-medium text-white transition hover:bg-[#05b34c]">
        เพิ่มเพื่อน LINE
      </a>
    </div>
  </header>
);

export default function LinePage() {
  return (
    <main className="min-h-screen bg-white text-[#15130F]">
      <Helmet>
        <title>KHORN LINE | ผู้ช่วยกฎหมายใน LINE สำหรับถาม ร่าง และเช็กงาน</title>
        <meta
          name="description"
          content="KHORN LINE ผู้ช่วยกฎหมายใน LINE สำหรับถามกฎหมาย ร่างเอกสาร เช็กงาน รับแจ้งเตือน และถามข้อมูลลูกความจากมือถือ"
        />
        <meta name="keywords" content="KHORN LINE, LINE Legal AI, AI กฎหมาย LINE, ผู้ช่วยทนาย LINE, ร่างเอกสาร LINE" />
        <meta property="og:title" content="KHORN LINE | ผู้ช่วยกฎหมายใน LINE" />
        <meta property="og:description" content="ถามกฎหมาย ร่างเอกสาร เช็กงาน และรับแจ้งเตือนจาก LINE" />
        <meta property="og:url" content="https://www.khorn-ai.com/line" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.khorn-ai.com/line" />
      </Helmet>

      <Header />

      <section className="px-5 py-20 md:px-10 md:py-28">
        <div className="mx-auto grid max-w-[1180px] items-center gap-14 lg:grid-cols-[0.95fr_1.05fr]">
          <div>
            <div className="mb-6 inline-flex items-center gap-3 rounded-full bg-[#F2FBF4] px-4 py-2">
              <span className="grid h-8 w-8 place-items-center rounded-xl bg-[#06C755] text-[10px] font-black text-white">LINE</span>
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#18A64A]">
                KHORN LINE
              </span>
            </div>
            <h1 className="max-w-4xl text-6xl font-semibold leading-[0.95] tracking-[-0.07em] md:text-8xl">
              ถาม ร่าง และเช็กงานจาก LINE
            </h1>
            <p className="mt-7 max-w-2xl text-xl leading-9 text-[#5F665E]">
              สำหรับงานประจำวันที่ไม่ต้องเปิดระบบใหญ่ ใช้จากมือถือได้ทันที ทั้งถามกฎหมาย ร่างเอกสาร เช็กงาน และรับแจ้งเตือน
            </p>
            <a
              href={lineOfficialUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-9 inline-flex h-14 items-center justify-center gap-3 rounded-xl bg-[#06C755] px-8 text-base font-semibold text-white shadow-[0_8px_18px_rgba(6,199,85,0.22)] transition hover:bg-[#05b34c]"
            >
              เพิ่มเพื่อน LINE Official
              <ArrowRight size={18} />
            </a>
          </div>

          <LineMock />
        </div>
      </section>

      <section className="border-y border-[#E8EAE5] bg-[#FAFAF7] px-5 py-20 md:px-10 md:py-24">
        <div className="mx-auto max-w-[1180px]">
          <div className="mb-12 max-w-4xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#6A7168]">ทำอะไรได้บ้าง</p>
            <h2 className="mt-5 text-5xl font-semibold leading-[0.98] tracking-[-0.06em] md:text-7xl">
              งานเล็ก ๆ ที่ทีมถามได้ทันทีจากมือถือ
            </h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {capabilities.map(([title, desc, Icon]) => (
              <article key={title} className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(22,29,24,0.06)]">
                <Icon size={28} className="text-[#18A64A]" />
                <h3 className="mt-8 text-2xl font-semibold tracking-[-0.04em]">{title}</h3>
                <p className="mt-4 text-base leading-8 text-[#5F665E]">{desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-20 md:px-10 md:py-24">
        <div className="mx-auto flex max-w-[1180px] flex-col items-center justify-between gap-6 rounded-[32px] bg-[#15130F] p-8 text-white md:flex-row md:p-10">
          <div>
            <h2 className="text-3xl font-semibold tracking-[-0.04em]">เริ่มใช้ KHORN จาก LINE</h2>
            <p className="mt-3 text-white/60">เพิ่มเพื่อนบัญชี LINE ทางการ แล้วเริ่มถามหรือสั่งงานจากมือถือ</p>
          </div>
          <a href={lineOfficialUrl} target="_blank" rel="noopener noreferrer" className="inline-flex h-14 shrink-0 items-center justify-center rounded-xl bg-[#06C755] px-8 text-base font-semibold text-white">
            เพิ่มเพื่อน LINE
          </a>
        </div>
      </section>
    </main>
  );
}

function LineMock() {
  return (
    <article className="mx-auto w-full max-w-sm">
      <div className="rounded-[44px] bg-[#0D0F0E] p-[10px] shadow-[0_28px_70px_rgba(0,0,0,0.22)]">
        <div className="overflow-hidden rounded-[34px] bg-white">
          <div className="px-5 py-4">
            <div className="mb-4 flex items-center justify-between text-xs font-semibold">
              <span>14:45</span>
              <span>Wi-Fi 42%</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl">‹</span>
                <span className="text-base font-semibold">99+</span>
                <div>
                  <p className="text-lg font-semibold leading-none">ค้อน เอไอ</p>
                  <p className="mt-1 text-xs text-[#747B72]">ผู้ช่วยกฎหมาย</p>
                </div>
              </div>
              <span className="text-xl">☰</span>
            </div>
          </div>

          <div className="min-h-[460px] bg-[#8EADD5] px-4 py-5">
            <div className="ml-auto max-w-[82%] rounded-[20px] rounded-tr-md bg-[#61E86D] px-4 py-3 text-sm leading-6">
              ช่วยร่างหนังสือทวงถามจากข้อมูลคดีนี้
            </div>
            <div className="mt-5 flex items-start gap-2">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white">
                <img src="/logo.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain" />
              </span>
              <div className="max-w-[82%] rounded-[20px] rounded-tl-md bg-white px-4 py-3 text-sm leading-6">
                หนูเตรียมร่างแรกให้แล้วค่ะ ใช้ข้อมูลลูกความ ประเด็นผิดสัญญา และหลักฐานการโอนเงินเป็นฐาน
              </div>
            </div>
            <div className="mt-5 overflow-hidden rounded-[22px] bg-white">
              <div className="bg-[#0D0F0E] px-4 py-4 text-white">
                <p className="text-xs font-semibold tracking-[0.12em] text-[#A7E6B0]">KHORN</p>
                <h3 className="mt-2 text-2xl font-semibold leading-tight">ร่างเอกสาร</h3>
                <p className="mt-1 text-sm text-[#BDEFC5]">พร้อมให้ทีมตรวจ</p>
              </div>
              <div className="px-4 py-4 text-sm leading-6 text-[#343834]">
                <p>1. สรุปข้อเท็จจริง</p>
                <p>2. ระบุยอดค้างชำระ</p>
                <p>3. แจ้งกำหนดชำระและสิทธิทางกฎหมาย</p>
                <a href={lineOfficialUrl} className="mt-4 block rounded-xl bg-[#18A64A] px-4 py-3 text-center text-sm font-medium text-white">
                  เปิดใน LINE
                </a>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 bg-white px-3 py-3">
            <span className="text-2xl">＋</span>
            <div className="flex-1 rounded-full bg-[#F4F4F4] px-4 py-2 text-[#B5B5B5]">Aa</div>
            <MessageSquareText size={20} />
          </div>
        </div>
      </div>
    </article>
  );
}
